import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { fmtNum } from "@/utils/toEnglishDigits";
import { useAuth } from "@/context/AuthContext";
import {
  Car as CarIcon,
  Users as UsersIcon,
  ArrowRight,
  X as XIcon,
  CaretLeft,
  CaretRight,
  ArrowsOut,
  Pencil,
  CurrencyCircleDollar,
} from "@phosphor-icons/react";

// ─── Lightbox with prev/next ─────────────────────────────────────────
function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    if (index == null) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onPrev();
      if (e.key === "ArrowLeft") onNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, onClose, onPrev, onNext]);

  if (index == null || !images?.length) return null;
  const src = images[index];
  const hasMore = images.length > 1;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.94)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="lightbox"
    >
      <button
        type="button"
        aria-label="إغلاق"
        onClick={onClose}
        className="absolute top-5 left-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white inline-flex items-center justify-center z-10"
        data-testid="lightbox-close"
      >
        <XIcon size={20} weight="bold" />
      </button>

      <div className="absolute top-5 right-5 z-10 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold num" dir="ltr">
        {fmtNum(index + 1)} / {fmtNum(images.length)}
      </div>

      {hasMore && (
        <>
          <button
            type="button"
            aria-label="السابق"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-primary/60 text-white inline-flex items-center justify-center z-10 transition-colors"
            data-testid="lightbox-prev"
          >
            <CaretRight size={22} weight="bold" />
          </button>
          <button
            type="button"
            aria-label="التالي"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-primary/60 text-white inline-flex items-center justify-center z-10 transition-colors"
            data-testid="lightbox-next"
          >
            <CaretLeft size={22} weight="bold" />
          </button>
        </>
      )}

      <img
        src={src}
        alt="car"
        className="max-w-[92vw] max-h-[88vh] rounded-2xl shadow-2xl select-none"
        style={{ objectFit: "contain" }}
        onClick={(e) => e.stopPropagation()}
        draggable={false}
      />
    </div>
  );
}

export default function CarDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [active, setActive] = useState(0);
  const [zoom, setZoom] = useState(null); // index or null

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/cars/${id}`);
      setCar(data);
      setActive(0);
    } catch (err) {
      setError(err?.response?.status === 404 ? "notfound" : formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const bookThis = () => {
    if (!car) return;
    navigate(`/booking/new?carId=${encodeURIComponent(car.id)}`);
  };

  // ─── Swipe gestures on hero (RTL: right→prev, left→next) ──────────
  const touchStartX = useRef(null);
  const onTouchStart = (e) => { touchStartX.current = e.touches?.[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartX.current;
    const dx = endX - touchStartX.current;
    touchStartX.current = null;
    if (Math.abs(dx) < 40) return;
    if (dx > 0) setActive((i) => (i - 1 + images.length) % images.length);
    else setActive((i) => (i + 1) % images.length);
  };

  // ─── States ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground" dir="rtl">
        <TopBar />
        <main className="max-w-5xl mx-auto p-6 text-center text-primary animate-pulse">
          جارٍ تحميل السيارة…
        </main>
      </div>
    );
  }

  if (error === "notfound") {
    return (
      <div className="min-h-screen bg-background text-foreground" dir="rtl">
        <TopBar />
        <main className="max-w-2xl mx-auto p-8 text-center">
          <div className="rounded-2xl bg-card border border-destructive/40 p-10">
            <CarIcon size={44} weight="duotone" className="text-destructive mx-auto mb-3" />
            <div className="text-white text-xl font-bold">السيارة غير موجودة</div>
            <div className="text-white/50 text-sm mt-2">
              الرابط قد يكون قديماً أو تم حذف السيارة.
            </div>
            <Link
              to="/admin/cars"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 mt-6 rounded-full bg-primary text-primary-foreground font-bold text-sm"
              data-testid="back-to-cars"
            >
              <ArrowRight size={14} weight="bold" /> العودة لقائمة السيارات
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background text-foreground" dir="rtl">
        <TopBar />
        <main className="max-w-2xl mx-auto p-8 text-center text-destructive">
          {error}
          <button
            onClick={() => { setError(null); load(); }}
            className="block mx-auto mt-4 px-4 py-2 rounded-full border border-primary/30 text-primary hover:bg-primary/10 text-sm font-bold"
          >
            إعادة المحاولة
          </button>
        </main>
      </div>
    );
  }

  const images = Array.isArray(car.images) && car.images.length
    ? car.images
    : (car.image_url ? [car.image_url] : []);
  const hasImages = images.length > 0;
  const hasThumbs = images.length > 1;
  const activeIdx = Math.min(active, Math.max(0, images.length - 1));
  const mainImg = hasImages ? images[activeIdx] : null;
  const passengers = car.max_passengers ?? car.capacity ?? 4;

  return (
    <div className="min-h-screen bg-background text-foreground pb-24 lg:pb-6" dir="rtl">
      <TopBar />
      <main className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header — Back + (Admin edit) */}
        <div className="flex items-center gap-3 mb-5">
          <button
            onClick={() => navigate(-1)}
            data-testid="car-back"
            className="w-9 h-9 rounded-full border border-primary/30 text-primary hover:bg-primary/10 inline-flex items-center justify-center"
          >
            <ArrowRight size={16} weight="bold" />
          </button>
          <div className="flex-1" />
          {isAdmin && (
            <Link
              to="/admin/cars"
              data-testid="car-admin-edit"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-primary/40 text-primary hover:bg-primary/10 text-xs font-bold"
            >
              <Pencil size={13} weight="bold" /> تعديل السيارة
            </Link>
          )}
        </div>

        {/* Gallery — Hero */}
        <div
          className="rounded-2xl bg-card border border-primary/25 overflow-hidden fadeup mb-5"
          data-testid="car-gallery"
        >
          <div
            className="relative bg-[#0B1A3A] aspect-[16/10] flex items-center justify-center cursor-zoom-in select-none"
            onClick={() => mainImg && setZoom(activeIdx)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            data-testid="car-main-image"
          >
            {mainImg ? (
              <img src={mainImg} alt={car.name_ar} className="w-full h-full object-cover" draggable={false} />
            ) : (
              <CarIcon size={100} weight="duotone" className="text-primary/25" />
            )}

            {hasThumbs && (
              <>
                <button
                  type="button"
                  aria-label="السابق"
                  onClick={(e) => { e.stopPropagation(); setActive((i) => (i - 1 + images.length) % images.length); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#050A15]/70 border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground inline-flex items-center justify-center transition-colors"
                  data-testid="car-gallery-prev"
                >
                  <CaretRight size={18} weight="bold" />
                </button>
                <button
                  type="button"
                  aria-label="التالي"
                  onClick={(e) => { e.stopPropagation(); setActive((i) => (i + 1) % images.length); }}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#050A15]/70 border border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground inline-flex items-center justify-center transition-colors"
                  data-testid="car-gallery-next"
                >
                  <CaretLeft size={18} weight="bold" />
                </button>
              </>
            )}

            {hasImages && (
              <>
                <div className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] font-bold px-2 py-1 rounded-full bg-[#050A15]/70 border border-primary/30 text-primary" dir="ltr">
                  <ArrowsOut size={12} weight="bold" /> عرض كامل
                </div>
                {hasThumbs && (
                  <div className="absolute top-3 right-3 text-[11px] font-bold px-2 py-1 rounded-full bg-[#050A15]/70 border border-primary/30 text-primary num" dir="ltr">
                    {fmtNum(activeIdx + 1)} / {fmtNum(images.length)}
                  </div>
                )}
              </>
            )}
          </div>
          {hasThumbs && (
            <div className="p-3 flex gap-2 overflow-x-auto border-t border-primary/10" data-testid="car-thumbs">
              {images.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActive(i)}
                  data-testid={`car-thumb-${i}`}
                  className={`shrink-0 w-24 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    activeIdx === i ? "border-primary shadow-[0_0_0_2px_rgba(201,169,97,0.35)]" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={src} alt={`${car.name_ar} ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info (minimal) */}
        <div className="text-center px-2 pt-2 pb-4 fadeup" data-testid="car-info">
          <h1
            className="text-white text-3xl md:text-4xl font-bold mb-3"
            data-testid="car-name"
          >
            {car.name_ar}
          </h1>
          {car.name_en && (
            <div className="text-white/40 text-xs uppercase tracking-widest mb-4">
              {car.name_en}
            </div>
          )}

          <div className="inline-flex items-center gap-2 mt-1 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-base font-bold" data-testid="car-passengers">
            <UsersIcon size={16} weight="fill" />
            <span className="num" dir="ltr">{fmtNum(passengers)}</span>
            <span>ركاب</span>
          </div>

          <div
            className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0F2148] border border-primary/25 text-white/80 text-sm font-semibold"
            data-testid="car-price-note"
          >
            <CurrencyCircleDollar size={15} weight="duotone" className="text-primary" />
            السعر: حسب المسار
          </div>

          {car.description && (
            <p
              className="text-white/75 text-sm md:text-base leading-relaxed max-w-2xl mx-auto mt-6 whitespace-pre-line"
              data-testid="car-description"
            >
              {car.description}
            </p>
          )}
        </div>

        {/* Desktop book button */}
        <div className="hidden lg:block mt-6 max-w-md mx-auto">
          <button
            onClick={bookThis}
            data-testid="book-this-car"
            className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-2xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-black text-base shadow-[0_10px_30px_rgba(201,169,97,0.25)] transition-all"
            style={{ minHeight: 56 }}
          >
            <CarIcon size={20} weight="fill" /> احجز هذه السيارة
          </button>
        </div>
      </main>

      {/* Mobile sticky book bar */}
      <div className="fixed bottom-0 inset-x-0 lg:hidden bg-[#050A15]/95 backdrop-blur border-t border-primary/25 p-3 z-40">
        <button
          onClick={bookThis}
          data-testid="book-this-car-mobile"
          className="w-full inline-flex items-center justify-center gap-2 py-3.5 rounded-full bg-primary text-primary-foreground font-black text-sm shadow-[0_10px_30px_rgba(201,169,97,0.25)]"
          style={{ minHeight: 56 }}
        >
          <CarIcon size={18} weight="fill" /> احجز هذه السيارة
        </button>
      </div>

      <Lightbox
        images={images}
        index={zoom}
        onClose={() => setZoom(null)}
        onPrev={() => setZoom((i) => (i - 1 + images.length) % images.length)}
        onNext={() => setZoom((i) => (i + 1) % images.length)}
      />
    </div>
  );
}
